/**
 * siteTime — the ONE way Time & Labor shows or reads a punch time.
 *
 * Every punch time is shown on the WORKER'S clock (the time zone of the market the
 * building is in, handed over by the database as `site_tz`), labelled CT / MT / ET / PT.
 * The viewer's own browser clock is never used — not for display, not for input
 * (Ben, 2026-09-24, Decisions 1–4).
 *
 * The label map mirrors the database's fn_time_tz_label exactly. If one changes, both change.
 */

export const DEFAULT_SITE_TZ = 'America/Chicago';

const TZ_LABEL: Record<string, string> = {
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/New_York': 'ET',
  'America/Los_Angeles': 'PT',
};

const TZ_LONG_NAME: Record<string, string> = {
  'America/Chicago': 'Central time',
  'America/Denver': 'Mountain time',
  'America/New_York': 'Eastern time',
  'America/Los_Angeles': 'Pacific time',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** CT / MT / ET / PT. Unknown zones fall back to the IANA name, as the database does. */
export function tzLabel(tz: string | null | undefined): string {
  const z = tz || DEFAULT_SITE_TZ;
  return TZ_LABEL[z] ?? z;
}

/** "Central time (CT)" — for the hint under a time field. */
export function tzLongLabel(tz: string | null | undefined): string {
  const z = tz || DEFAULT_SITE_TZ;
  return TZ_LONG_NAME[z] ? `${TZ_LONG_NAME[z]} (${tzLabel(z)})` : z;
}

type Parts = { year: number; month: number; day: number; hour: number; minute: number; second: number };

const partsCache = new Map<string, Intl.DateTimeFormat>();
function fmtFor(tz: string): Intl.DateTimeFormat {
  let f = partsCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    partsCache.set(tz, f);
  }
  return f;
}

/** Wall-clock parts of an instant, on the given zone's clock. */
function wallParts(ms: number, tz: string): Parts {
  const out: Record<string, number> = {};
  for (const p of fmtFor(tz).formatToParts(new Date(ms))) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return {
    year: out.year, month: out.month, day: out.day,
    hour: out.hour === 24 ? 0 : out.hour, minute: out.minute, second: out.second,
  };
}

function toMs(iso: string | Date): number | null {
  if (iso == null || iso === '') return null;
  const ms = iso instanceof Date ? iso.getTime() : Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

function clock12(h: number, m: number): string {
  const suffix = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** "7:52 PM CT" — or '' when there is no time (an open shift's out, say). */
export function formatSiteTime(iso: string | Date | null | undefined, tz: string | null | undefined): string {
  const ms = toMs(iso as string);
  if (ms == null) return '';
  const z = tz || DEFAULT_SITE_TZ;
  const p = wallParts(ms, z);
  return `${clock12(p.hour, p.minute)} ${tzLabel(z)}`;
}

/** "Sep 21, 7:52 PM CT" */
export function formatSiteDateTime(iso: string | Date | null | undefined, tz: string | null | undefined): string {
  const ms = toMs(iso as string);
  if (ms == null) return '';
  const z = tz || DEFAULT_SITE_TZ;
  const p = wallParts(ms, z);
  return `${MONTHS[p.month - 1]} ${p.day}, ${clock12(p.hour, p.minute)} ${tzLabel(z)}`;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * "Tuesday, 10/6/26, 11:23 PM CT" — the punch-time shape of Buddy Punch's In/Out
 * Activity export, which payroll reads every period (In/Out Activity report, 2026-10-16).
 * Same wall clock and label as every other formatter here; only the layout differs.
 * The weekday is taken from the wall-clock date, never from the viewer's Date().
 */
export function formatSitePunchStamp(iso: string | Date | null | undefined, tz: string | null | undefined): string {
  const ms = toMs(iso as string);
  if (ms == null) return '';
  const z = tz || DEFAULT_SITE_TZ;
  const p = wallParts(ms, z);
  const weekday = WEEKDAYS[new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay()];
  return `${weekday}, ${p.month}/${p.day}/${String(p.year % 100).padStart(2, '0')}, ${clock12(p.hour, p.minute)} ${tzLabel(z)}`;
}

/**
 * "2026-09-21" → "Sep 21". A shift date is a calendar date, not an instant: it never
 * goes through new Date(), which reads it as UTC midnight and shows the previous day
 * in every US browser.
 */
export function formatShiftDate(ymd: string | null | undefined): string {
  if (!ymd) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!m) return ymd;
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
}

/**
 * The value for an <input type="datetime-local"> showing this instant on the site's clock:
 * "2026-09-21T19:52".
 */
export function isoToSiteInput(iso: string | Date | null | undefined, tz: string | null | undefined): string {
  const ms = toMs(iso as string);
  if (ms == null) return '';
  const p = wallParts(ms, tz || DEFAULT_SITE_TZ);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * A typed wall-clock time ("2026-09-25T20:00", or "2026-09-25" + "20:00") read on the
 * SITE's clock, returned as a UTC ISO string for the database.
 *
 * DST, matching Postgres's own rule for `timestamp AT TIME ZONE`: a time that happens
 * twice (fall back) takes the offset in force just AFTER the change (the later instant);
 * a time that does not exist (spring forward) takes the offset in force just BEFORE it
 * (so 2:30 AM becomes 3:30 AM).
 */
export function siteInputToISO(value: string, tz: string | null | undefined, time?: string): string | null {
  if (!value) return null;
  const raw = time ? `${value}T${time}` : value;
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(raw.trim());
  if (!m) return null;
  const z = tz || DEFAULT_SITE_TZ;
  const [y, mo, d, h, mi, s] = [m[1], m[2], m[3], m[4], m[5], m[6] ?? '0'].map(Number);
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi, s);

  // Offset of the zone at a given instant, in ms (wall − utc).
  const offsetAt = (ms: number) => {
    const p = wallParts(ms, z);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - ms;
  };

  // Candidates from the offsets either side of the wall time; keep the ones that
  // round-trip. Two hits = fall-back ambiguity → the later instant (Postgres's rule).
  const offs = [offsetAt(wallAsUtc - 12 * 3600e3), offsetAt(wallAsUtc + 12 * 3600e3)];
  const hits = offs
    .map((o) => wallAsUtc - o)
    .filter((ms) => offsetAt(ms) === wallAsUtc - ms)
    .sort((a, b) => a - b);
  if (hits.length) return new Date(hits[hits.length - 1]).toISOString();

  // Nonexistent (spring-forward gap): use the pre-transition offset, which lands an hour later.
  return new Date(wallAsUtc - Math.min(...offs)).toISOString();
}
